import { forwardRef } from 'react';
import { css } from '@emotion/react';
import { PersonTypes } from '@frontend/api-person';
import { SubscriptionsTypes } from '@frontend/api-subscription';
import { useTranslation } from '@frontend/i18n';
import { useFeatureSubscriptionConfig } from '@frontend/self-upgrade';
import { theme } from '@frontend/theme';
import { Button, Heading, ModalControlResponse, useModalControl } from '@frontend/design-system';
import { TrialNudgeBanner } from '../free-trial-nudges/trial-nudge-banner';
import { useFreeTrialNudge } from '../hooks/free-trial-nudge-hooks/use-free-trial-tooltip';
import { useInlineReport } from '../hooks/use-inline-report';
import { useInsuranceContext } from '../providers/insurance-provider';
import { useInsuranceType } from '../providers/insurance-type-provider';
import { insuranceTabTrackingIds } from '../tracking-ids';
import { DateRangeOptionValue } from '../utils/constants';
import { shouldShowFreeTrialNudge } from '../utils/free-trial-nudge-utils';
import { InsuranceDetails } from '../utils/types';
import { constructPatientsFullName } from '../utils/utils';
import { ActionsMenu } from './actions-menu';
import { ErrorBanner } from './error-banner';
import { InsuranceCardBannerNotification } from './insurance-card-banner-notification';
import { InsuranceDetailList } from './insurance-details-list';
import { InsuranceInlineReport } from './insurance-inline-report';
import { InsuranceLoader } from './insurance-loader';
import { InsuranceSetProviderPinBanner } from './insurance-set-provider-pin-banner';
import { InsuranceStatus } from './insurance-status';
import { StyledCardContainer } from './styled-card-container';
import { VerificationMetricSidePanel } from './verification-metric-side-panel';
import { VerifiedInsuranceDetailsList } from './verified-insurance-details-list';
import { VerifyInsuranceSidePanel } from './verify-insurance-side-panel';
import { WritebackSidePanel } from './write-back-side-panel';

interface InsuranceProps {
  defaultDateRange: DateRangeOptionValue;
  verificationMetricModalControl: ModalControlResponse;
  isIntegratedOffice?: boolean;
  updateDefaultDateRange: (range: DateRangeOptionValue) => void;
}
export const Insurance = forwardRef<HTMLElement, InsuranceProps>(
  ({ defaultDateRange, verificationMetricModalControl, isIntegratedOffice, updateDefaultDateRange }, ref) => {
    const { t } = useTranslation();
    const verifyInsuranceModalControl = useModalControl();
    const writeBackModal = useModalControl();
    const { nudgeProps, triggerProps, middlewareData, isFreeTrialNudgeOpen, setFreeTrialNudgeOpenState } =
      useFreeTrialNudge();
    const { switchToLargeModalProps, switchToLargeModalTriggerProps, isReportOpen, setIsReportOpen } =
      useInlineReport();
    const {
      locationId,
      personInfo,
      insuranceDetails,
      payersList,
      hasActiveOrVerifiedInsurance,
      hasInactiveInsuranceDetailsFromPartner,
      isEnhancedInsuranceVerificationEnabled,
      hasErrors,
      shouldShowTuuthFairyReport,
      hasCoverage,
      insuranceVerificationAPIResData,
      isInsuranceDetailsWritebackSupported,
      requestId,
      isRefetching,
      isDental,
      errorDetails,
      refetchInsuranceDetails,
    } = useInsuranceContext();
    const { insuranceType } = useInsuranceType();

    const shouldShowWriteBack =
      isInsuranceDetailsWritebackSupported &&
      personInfo?.SourceType === PersonTypes.PersonSourceType.Integration &&
      isDental &&
      hasActiveOrVerifiedInsurance;

    const subscriptionConfig = useFeatureSubscriptionConfig({
      feature: SubscriptionsTypes.Feature.ENHANCED_INSURANCE_VERIFICATION,
      checkBundleUpgrade: false,
    });
    //sync button and retrieving notification will only be shown in progress state for enhanced verification
    const enhancedVerificationInprogress =
      isEnhancedInsuranceVerificationEnabled && insuranceDetails?.verificationStatus === 'IN_PROGRESS';
    const showVerifyButton = (hasErrors || !hasCoverage) && !enhancedVerificationInprogress;
    const showSyncButton = enhancedVerificationInprogress;

    // Free Trial is enabled only for dental and offices who do not have enhanced eligibility
    const shouldRenderFreeTrialNudge =
      isDental &&
      !isEnhancedInsuranceVerificationEnabled &&
      isFreeTrialNudgeOpen &&
      subscriptionConfig.isFreeTrialEligible &&
      shouldShowFreeTrialNudge();

    const onViewInsuranceReportClick = () => {
      setIsReportOpen(true);
    };

    return (
      <StyledCardContainer>
        <section ref={ref} css={{ padding: theme.spacing(2) }}>
          <header css={{ display: 'flex', justifyContent: 'space-between', marginBottom: theme.spacing(1) }}>
            <div css={headingContainerStyles} {...triggerProps}>
              <Heading level={3}>{hasActiveOrVerifiedInsurance ? t('Payer Details') : t('Insurance')}</Heading>
              <InsuranceStatus
                enhancedVerificationInprogress={enhancedVerificationInprogress}
                coverageStatus={insuranceDetails?.coverageStatus}
                hasErrors={hasErrors}
                locationId={locationId}
                onVerifyClick={verifyInsuranceModalControl.openModal}
              />
            </div>
            <div css={headingContainerStyles}>
              {showSyncButton && (
                <Button
                  trackingId={insuranceTabTrackingIds.enhancedEligibilityRefreshButton}
                  size='large'
                  iconName='sync'
                  variant='secondary'
                  disabled={isRefetching}
                  onClick={() => refetchInsuranceDetails?.()}
                />
              )}
              <ActionsMenu
                shouldShowTuuthFairyReport={shouldShowTuuthFairyReport}
                verificationStatus={insuranceDetails?.thirdPartyCoverageStatus}
                verifyInsuranceModalControl={verifyInsuranceModalControl}
                onVerificationMetricClick={() => updateDefaultDateRange('today')}
                shouldShowWriteBack={shouldShowWriteBack}
                writeBackModal={writeBackModal}
                requestId={requestId}
                onViewReportClick={onViewInsuranceReportClick}
              />
            </div>
          </header>
          {/* isRefetching will be true when user hit sync icon to fetch latest data */}
          {isRefetching ? (
            <InsuranceLoader />
          ) : (
            <>
              {!insuranceDetails?.shouldShowOverriddenStatus && (
                <>
                  <ErrorBanner
                    isRetryButtonVisible //should be person data
                    errorDetails={errorDetails}
                    onRetryClick={verifyInsuranceModalControl.openModal}
                  />

                  <InsuranceCardBannerNotification
                    isEligibleForWebsiteResp={!!insuranceDetails?.isElegibleForWebsiteResp}
                    enhancedVerificationInprogress={enhancedVerificationInprogress}
                    locationId={locationId}
                    payerID={insuranceDetails?.payerId || ''}
                  />

                  <InsuranceSetProviderPinBanner
                    insuranceVerificationCoverageDetails={
                      insuranceVerificationAPIResData?.coverage?.thirdPartyCoverage || {}
                    }
                    locationId={locationId}
                  />
                </>
              )}
              {hasActiveOrVerifiedInsurance || hasInactiveInsuranceDetailsFromPartner ? (
                <>
                  <VerifiedInsuranceDetailsList
                    insuranceVerificationAPIResData={insuranceVerificationAPIResData}
                    locationId={locationId || ''}
                    pmsPayerId={insuranceDetails?.pmsPayerId || ''}
                    isIntegratedOffice={isIntegratedOffice}
                    payersList={payersList}
                    isEnhancedInsuranceVerificationEnabled={isEnhancedInsuranceVerificationEnabled}
                  />
                  <Button
                    trackingId={insuranceTabTrackingIds.viewReportButton}
                    variant='tertiary'
                    css={css({
                      width: '100%',
                      marginTop: theme.spacing(1),
                    })}
                    onClick={onViewInsuranceReportClick}
                    {...switchToLargeModalTriggerProps}
                  >
                    {t('View Report')}
                  </Button>
                  <InsuranceInlineReport
                    locationId={locationId}
                    sourceId={personInfo?.SourceID}
                    personInfo={{
                      personId: personInfo?.PersonID || '',
                      personName: constructPatientsFullName(personInfo?.FirstName, personInfo?.LastName),
                    }}
                    shouldShowTuuthFairyReport={shouldShowTuuthFairyReport}
                    personPMSId={personInfo?.PMID}
                    isReportOpen={isReportOpen}
                    setIsReportOpen={setIsReportOpen}
                    switchToLargeModalProps={switchToLargeModalProps}
                    insuranceType={insuranceType}
                  />
                </>
              ) : (
                <>
                  <InsuranceDetailList
                    isIntegratedOffice={isIntegratedOffice}
                    insuranceDetails={insuranceDetails as InsuranceDetails}
                  />
                  {showVerifyButton && (
                    <Button
                      trackingId={insuranceTabTrackingIds.verifyManuallyButton}
                      variant='tertiary'
                      css={css({
                        width: '100%',
                        marginTop: theme.spacing(1),
                      })}
                      onClick={verifyInsuranceModalControl.openModal}
                    >
                      {t('Verify Manually')}
                    </Button>
                  )}
                </>
              )}
              {shouldRenderFreeTrialNudge && (
                <TrialNudgeBanner
                  nudgeProps={nudgeProps}
                  middleWareData={middlewareData}
                  setFreeTrialNudgeOpenState={setFreeTrialNudgeOpenState}
                  locationId={locationId || ''}
                />
              )}
            </>
          )}
        </section>
        <WritebackSidePanel {...writeBackModal.modalProps} />
        <VerifyInsuranceSidePanel
          {...verifyInsuranceModalControl.modalProps}
          hasVerificationError={!!hasErrors}
          shouldShowTuuthFairyReport={shouldShowTuuthFairyReport}
          onReportLearnMoreClick={() => updateDefaultDateRange('this-month')}
        />
        <VerificationMetricSidePanel {...verificationMetricModalControl.modalProps} dateRange={defaultDateRange} />
      </StyledCardContainer>
    );
  }
);
Insurance.displayName = 'Insurance';

const headingContainerStyles = css({ display: 'flex', alignItems: 'center', gap: theme.spacing(1) });
